import { useEffect, useRef, useState } from "react";

const LAYOUT_KEY = "eperion.layout";
const PHONE_QUERY = "(max-width: 767px)";

function requestedLayout() {
  const params = new URLSearchParams(window.location.search);
  const layout = params.get("layout");
  if (layout === "phone" || layout === "desktop") return layout;
  if (params.get("phone") === "1") return "phone";
  if (params.get("phone") === "0") return "desktop";
  return null;
}

function readLayout(matchesPhone) {
  try {
    const saved = localStorage.getItem(LAYOUT_KEY);
    if (saved === "phone" || saved === "desktop") return saved;
  } catch (error) {
    // Private mode can block storage. Fall through to the screen size.
  }
  return matchesPhone ? "phone" : "desktop";
}

export function useLayoutMode() {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "desktop";
    const requested = requestedLayout();
    if (requested) {
      try {
        localStorage.setItem(LAYOUT_KEY, requested);
      } catch (error) {
        // Private mode can block storage. The choice still applies this load.
      }
      return requested;
    }
    return readLayout(window.matchMedia(PHONE_QUERY).matches);
  });

  useEffect(() => {
    const requested = requestedLayout();
    if (requested) {
      try {
        localStorage.setItem(LAYOUT_KEY, requested);
      } catch (error) {
        // Ignore storage failures and still apply the override for this load.
      }
    }
    const media = window.matchMedia(PHONE_QUERY);
    const apply = () => setMode(readLayout(media.matches));
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  return mode;
}

function viewKey(userId) {
  return `eperion.mode.${userId || "device"}`;
}

function readPhoneView(userId, fallback) {
  try {
    const saved = localStorage.getItem(viewKey(userId));
    if (saved === "technician" || saved === "office") return saved;
  } catch (error) {
    // Private mode can block storage. Use the role default.
  }
  return fallback === "technician" ? "technician" : "office";
}

export function usePhoneView(user, fallback = "office") {
  const userId = user?._id || (user?.id != null ? String(user.id) : "");
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const [view, setViewState] = useState(() => readPhoneView(userId, fallback));

  useEffect(() => {
    setViewState(readPhoneView(userId, fallbackRef.current));
  }, [userId]);

  const setView = (next) => {
    const value = next === "technician" ? "technician" : "office";
    try {
      localStorage.setItem(viewKey(userId), value);
    } catch (error) {
      // The choice still applies until reload.
    }
    setViewState(value);
  };

  return [view, setView];
}
